import '~/src/dash/api/console-api';

import { Grid, Paper } from '@material-ui/core';
import React from 'react';
import update from 'react-addons-update';

import { getDealTPAccounts, IDealTPAccount, updateDealTPAccounts } from '~/src/dash/api/deals';
import { ModelMediumButton } from '~/src/dash/components/model-medium-button';
import ModelMissingPermission from '~/src/dash/components/model-missing-permission';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { Panel, Panels } from '~/src/dash/publicaUI';
import { IStore } from '~/src/dash/reducers/store';
import { extractRouteParams, navigate, RouteDealList } from '~/src/dash/router/routes';
import {
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { publicaColors } from '~/src/dash/utils/colors';
import { decorate } from '~/src/dash/utils/decorator';
import {
  IPermissionCheck,
  IS_AUTHORIZED,
  notAllowedEmptyIPermissionCheck,
} from '~/src/dash/utils/permission_service';
import { routeName } from '~/src/dash/utils/routes';
import { DealTPAccountAdd } from '~/src/dash/views/deal/deal-tp-account-add';
import { DealTPAccountList } from '~/src/dash/views/deal/deal-tp-account-list';
import { LoadingView } from '~/src/dash/views/loading-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}

interface IState {
  loading: boolean;
  readAuthorization: IPermissionCheck;
  writeAuthorization: IPermissionCheck;
  snackbarMessage: string;
  snackbarError: boolean;
  snackbarWarning: boolean;
  dealTPAccounts: IDealTPAccount[];
  accountAreBeingSaved: boolean;
}

export const DealTPAccountView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) => ({
      paper: {
        ...theme.mixins.gutters(),
        paddingTop: theme.spacing.unit * 2,
        paddingBottom: theme.spacing.unit * 2,
        marginBottom: theme.spacing.unit * 4,
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    displayName: 'DealTPAccountView';

    constructor(props: IProps) {
      super(props);
      this.state = {
        dealTPAccounts: [] as IDealTPAccount[],
        loading: true,
        readAuthorization: notAllowedEmptyIPermissionCheck,
        writeAuthorization: notAllowedEmptyIPermissionCheck,
        snackbarMessage: '',
        snackbarError: false,
        snackbarWarning: false,
        accountAreBeingSaved: false,
      };
    }

    handleCloseSnackbar = (): void => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
        snackbarWarning: false,
      });
    };

    // Handle the click on the "Close" button. Go to previous page.
    handleClose() {
      navigate(
        RouteDealList,
        extractRouteParams({
          session: this.props.session,
          user: this.props.user,
        }),
      );
    }

    // Handle change in the third-party account list.
    handleChange(updatedDealTPAccount: IDealTPAccount[]): void {
      this.setState({
        dealTPAccounts: updatedDealTPAccount,
      });
    }

    // addNewTPAccount creates a new empty third-party account in the account list with the given API name.
    addNewTPAccount(apiName: string): void {
      // Create new account entry.
      const newAccount: IDealTPAccount = {
        APIName: apiName,
      } as IDealTPAccount;
      // Add it to the list of accounts.
      this.setState({
        dealTPAccounts: update(this.state.dealTPAccounts, { $push: [newAccount] }),
      });
    }

    // Handle the click on the "Save" button. Push the new third-party account list in the API.
    handleSave() {
      // Display the "account are being saved" loading view.
      this.setState({
        accountAreBeingSaved: true,
      });

      // Call API to update.
      updateDealTPAccounts(this.state.dealTPAccounts, this.props.session.publisher.ID)
        // ---- API call is OK.
        .then((tpAccountList: IDealTPAccount[]) => {
          // If any, display the account auth errors in the snackbar.
          let shouldDisplayWarning = false; // If we should display a snackbar warning.
          let accountErrorMessage = 'Accounts are saved, but some authentication failed:'; // Warning message to display.
          const dealTPAccounts = this.state.dealTPAccounts;

          // for each account created/updated in API: find error(s) and, for newly created, assign ID
          for (const updatedAccount of tpAccountList) {
            // assign ID if the local account doesn't have one
            const localAccount =
              dealTPAccounts &&
              dealTPAccounts.find(
                (account: IDealTPAccount) => account.APIName === updatedAccount.APIName,
              );
            if (localAccount && !localAccount.ID) {
              localAccount.ID = updatedAccount.ID;
              localAccount.Password = updatedAccount.Password;
            }
            // find and add account error, if any
            if (updatedAccount.Error !== '') {
              shouldDisplayWarning = true;
              accountErrorMessage += `\n • ${updatedAccount.APIName} → ${updatedAccount.Error}`;
            }
          }

          // Show snackbar.
          this.setState({
            snackbarMessage: shouldDisplayWarning ? accountErrorMessage : 'Accounts are saved.',
            snackbarWarning: shouldDisplayWarning,
            accountAreBeingSaved: false,
            dealTPAccounts,
          });
        })
        // ---- API call returned an ERROR.
        .catch(() => {
          this.setState({
            snackbarMessage: 'Failed to save new account.',
            snackbarError: true,
            accountAreBeingSaved: false,
          });
        });
    }

    componentDidMount() {
      // Get variables from props.
      const { user, session } = this.props;

      // Get the user's permissions.
      this.setState({
        readAuthorization: IS_AUTHORIZED(['read_deal'], user.user),
        writeAuthorization: IS_AUTHORIZED(['write_deal'], user.user),
      });

      // Get the third-party accounts from the API.
      getDealTPAccounts(session.publisher.ID)
        .then((dealTPAccounts) => {
          this.setState({
            dealTPAccounts,
            loading: false,
          });
        })
        .catch(() => {
          this.setState({
            snackbarError: true,
            snackbarMessage: 'Error while getting the account list from the API',
          });
        });
    }

    render() {
      // Get the variables from the state/props.
      const {
        loading,
        snackbarError,
        snackbarMessage,
        snackbarWarning,
        dealTPAccounts,
        writeAuthorization,
        readAuthorization,
        accountAreBeingSaved,
      } = this.state;
      const { classes, user, session, location } = this.props;
      const { pathname } = location;

      // Handle the loading of the third-party accounts.
      if (loading) {
        return <LoadingView active={true} text='Loading deal third-party accounts.' />;
      }

      // Handle waiting while account are being saved.
      if (accountAreBeingSaved) {
        return (
          <LoadingView
            active={true}
            text='Accounts are being saved. Please wait: this can take up to a minute.'
          />
        );
      }

      // Handle when user is missing permissions.
      if (!readAuthorization.isAuthorized && !writeAuthorization.isAuthorized) {
        return (
          <ModelMissingPermission
            missingPermissions={[
              ...readAuthorization.missingPermissions,
              ...writeAuthorization.missingPermissions,
            ]}
          />
        );
      }

      // Handle "normal" view.
      return (
        <Panels maxWidth vertical relative minWidthMain>
          <Panel
            largeHorizontalPadding
            verticalPadding
            style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
          >
            <ModelTopBar
              title={routeName(pathname)}
              session={session}
              user={user}
              publisher={session.publisher}
              classes={classes}
              delegateElevate={true}
              currentPath={pathname}
              disableTitlePaddingLeft
            />
          </Panel>
          <Panel scroll largePadding>
            <Grid container direction='column' justify='flex-start' alignItems='center'>
              {/* Buttons row.*/}
              <Grid item style={{ margin: '10px 45px 12px', width: '-webkit-fill-available' }}>
                <Grid container direction='row' justify='space-between' alignItems='flex-start'>
                  {/* Close button*/}
                  <ModelMediumButton
                    variant='contained'
                    color='primary'
                    onClick={() => this.handleClose()}
                  >
                    Close
                  </ModelMediumButton>

                  {/* Save button*/}
                  <ModelMediumButton
                    variant='contained'
                    color='primary'
                    onClick={() => this.handleSave()}
                  >
                    Save
                  </ModelMediumButton>
                </Grid>
              </Grid>

              {/* List of accounts + add new button*/}
              <Paper style={{ margin: '0px 30px 0px', width: '-webkit-fill-available' }}>
                <Grid container direction={'column'} justify={'flex-start'} alignItems={'center'}>
                  {/* List of accounts.*/}
                  <Grid item style={{ width: '86%', marginTop: '16px' }}>
                    <DealTPAccountList
                      tpAccounts={dealTPAccounts}
                      onChange={(dl: IDealTPAccount[]) => this.handleChange(dl)}
                    />
                  </Grid>

                  {/* Dropdown to add a new account*/}
                  <Grid item style={{ width: '86%', marginTop: '33px' }}>
                    <DealTPAccountAdd
                      tpAccounts={dealTPAccounts}
                      addNewAccountHandler={(apiName: string) => this.addNewTPAccount(apiName)}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Display the snack-bar for notifications.*/}
            <SnackbarView
              open={!!snackbarMessage}
              error={!!snackbarError || !!snackbarWarning}
              onClose={this.handleCloseSnackbar}
              backgroundColor={snackbarWarning ? '#ffb100' : undefined}
            >
              {snackbarMessage}
            </SnackbarView>
          </Panel>
        </Panels>
      );
    }
  },
);
